import '../diseñocss/SectoresDark.css';
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import BotonAcceso from "../elementos/BotonAcceso";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from "@mui/icons-material/Delete";
import BookIcon from '@mui/icons-material/Book';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from "../guardarToken/themeContext";

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS;

function AbmPlatos() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [platos, setPlatos] = useState([]);
  const [tiposCategorias, setTiposCategorias] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});
  const [platoAEliminar, setPlatoAEliminar] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedTipo, setSelectedTipo] = useState(null);

  useEffect(() => {
    getPlatos();
  }, []);

  const getPlatos = async () => {
    try {
      setLoading(true);
      // Obtener platos y tipos en paralelo
      const [resPlatos, resTipos] = await Promise.all([
        axios.get(apiUrlPlatos),
        axios.get(apiUrlPlatos.replace(/\/platos\/?$/, '/tipos-menu'))
      ]);

      setPlatos(resPlatos.data);
      setTiposCategorias(resTipos.data);

      if (resTipos.data.length > 0) {
        setSelectedTipo(resTipos.data[0].nombre);
      } else if (resPlatos.data.length > 0) {
        // Fallback si no hay categorías definidas pero sí platos
        const uniqueTypes = [...new Set(resPlatos.data.map(p => p.tipo))];
        setSelectedTipo(uniqueTypes[0]);
      }
    } catch (error) {
      console.error("Error al obtener los datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const deletePlato = async () => {
    try {
      await axios.delete(`${apiUrlPlatos}${platoAEliminar.id_plato}`);
      setOpenDialog(false);
      setPlatoAEliminar(null);
      getPlatos();
    } catch (error) {
      console.error("Error al eliminar el plato:", error);
    }
  };

  const handleSearchChange = (e, tipo) => {
    const updatedSearchTerms = { ...searchTerms, [tipo]: e.target.value };
    setSearchTerms(updatedSearchTerms);
  };

  const tiposUnicos = [...new Set(platos.map((plato) => plato.tipo))];

  const countsPerTipo = tiposUnicos.reduce((acc, tipo) => {
    acc[tipo] = platos.filter(p => p.tipo === tipo).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className={`flex justify-center items-center h-screen ${isDarkMode ? "bg-slate-950" : "bg-[#f8f5e9]"}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-3 lg:p-4 font-sans overflow-x-hidden transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-[#f8f5e9] text-gray-800"}`}>
      {/* HEADER CARD - COMPACT */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-xl shadow-sm p-4 mb-4 flex flex-col md:flex-row items-center justify-between border animate-in slide-in-from-top duration-700`}>
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-2.5 rounded-lg shadow-lg">
            <BookIcon className="text-white" sx={{ fontSize: 24 }} />
          </div>
          <div>
            <h1 className={`text-lg font-black tracking-tight ${isDarkMode ? "text-white" : "text-gray-800"}`}>Gestión de Platos</h1>
            <p className={`text-[10px] ${isDarkMode ? "text-slate-400" : "text-gray-500"} font-black uppercase tracking-wider`}>Administra el menú y los platos disponibles</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3 md:mt-0">
          <BotonAcceso
            variant="accent"
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-1.5 flex items-center gap-2 text-[11px] font-bold normal-case shadow-sm transition-all hover:scale-105"
            onClick={() => navigate("/admin/AbmTiposMenu")}
          >
            <SettingsIcon sx={{ fontSize: 14 }} /> Gestionar Tipos
          </BotonAcceso>
          <BotonAcceso
            variant="accent"
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-3 py-1.5 flex items-center gap-2 text-[11px] font-bold normal-case shadow-sm transition-all hover:scale-105"
            onClick={() => navigate("/admin/CreatePlatos")}
          >
            <AddIcon sx={{ fontSize: 14 }} /> Crear Plato
          </BotonAcceso>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* SIDEBAR - COMPACT */}
        <div className="w-full lg:w-56 flex-shrink-0 animate-in slide-in-from-left duration-700">
          <div className="bg-orange-600 rounded-t-lg p-2.5 shadow-md">
            <h3 className="text-white font-black text-[10px] uppercase tracking-[0.15em]">Tipos de Plato</h3>
          </div>
          <div className="flex flex-col gap-1.5 pt-1">
            {tiposCategorias.length > 0 ? (
              tiposCategorias.map((tipo) => (
                <button
                  key={tipo.id}
                  onClick={() => setSelectedTipo(tipo.nombre)}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-all border ${selectedTipo === tipo.nombre
                    ? "bg-orange-600 text-white border-orange-500 shadow-lg scale-[1.02] font-black"
                    : isDarkMode
                      ? "bg-slate-900 text-slate-400 border-slate-800 hover:border-orange-500/50 hover:bg-orange-500/10"
                      : "bg-white text-gray-600 border-gray-100 hover:border-orange-200 hover:bg-orange-50 shadow-sm"
                    }`}
                >
                  <span className="truncate text-xs">{tipo.nombre}</span>
                  {selectedTipo === tipo.nombre ? (
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  ) : (
                    <span className={`${isDarkMode ? "bg-orange-950 text-orange-200" : "bg-orange-100 text-orange-700"} text-[9px] font-black px-1.5 py-0.5 rounded-full`}>
                      {platos.filter(p => p.tipo === tipo.nombre).length}
                    </span>
                  )}
                </button>
              ))
            ) : (
              [...new Set(platos.map(p => p.tipo))].map((tipo) => (
                <button
                  key={tipo}
                  onClick={() => setSelectedTipo(tipo)}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-all border ${selectedTipo === tipo
                    ? "bg-orange-600 text-white border-orange-500 shadow-lg scale-[1.02] font-black"
                    : isDarkMode
                      ? "bg-slate-900 text-slate-400 border-slate-800 hover:border-orange-500/50 hover:bg-orange-500/10"
                      : "bg-white text-gray-600 border-gray-100 hover:border-orange-200 hover:bg-orange-50 shadow-sm"
                    }`}
                >
                  <span className="truncate text-xs">{tipo}</span>
                  <span className={`${isDarkMode ? "bg-orange-950 text-orange-200" : "bg-orange-100 text-orange-700"} text-[9px] font-black px-1.5 py-0.5 rounded-full`}>
                    {platos.filter(p => p.tipo === tipo).length}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* MAIN CONTENT - COMPACT */}
        <div className="flex-1 min-w-0 animate-in slide-in-from-bottom duration-1000">
          {selectedTipo ? (
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-xl shadow-sm border overflow-hidden`}>
              <div className="bg-orange-600 p-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-black text-white uppercase tracking-tight">{selectedTipo}</h2>
                  <p className="text-orange-100 text-[10px] font-bold italic">{countsPerTipo[selectedTipo]} platos disponibles</p>
                </div>
              </div>

              <div className={`p-2.5 ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-50"} border-b`}>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <SearchIcon className={isDarkMode ? "text-slate-500" : "text-gray-400"} sx={{ fontSize: 16 }} />
                  </div>
                  <input
                    type="text"
                    style={{ paddingLeft: '48px' }}
                    className={`w-full pr-3 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-orange-500/30 ${isDarkMode
                      ? "bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                      : "bg-gray-50 border-gray-200 text-gray-700"
                      }`}
                    placeholder={`Buscar...`}
                    value={searchTerms[selectedTipo] || ''}
                    onChange={(e) => handleSearchChange(e, selectedTipo)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className={`${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-gray-50 text-gray-400"} text-[9px] uppercase font-bold tracking-wider`}>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-left">Descripción</th>
                      <th className="px-3 py-2 text-center w-20">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-gray-50"}`}>
                    {platos
                      .filter(p => p.tipo === selectedTipo)
                      .filter(p => {
                        const search = searchTerms[selectedTipo]?.toLowerCase() || "";
                        return search === "" || p.nombre.toLowerCase().includes(search);
                      })
                      .map((plato, idx) => (
                        <tr key={plato.id_plato} className={`${isDarkMode ? "hover:bg-slate-800/50" : "hover:bg-orange-50/20"} transition-colors ${idx % 2 === 0 ? '' : (isDarkMode ? 'bg-slate-900/50' : 'bg-gray-50/10')}`}>
                          <td className="px-3 py-2 font-black whitespace-nowrap" style={{ color: isDarkMode ? '#fb923c' : '#111827' }}>{plato.nombre}</td>
                          <td className={`px-3 py-2 text-[11px] font-bold truncate max-w-xs ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>{plato.descripcion || '-'}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center gap-1.5">
                              <button
                                onClick={() => navigate(`/admin/EditarPlatos/${plato.id_plato}`)}
                                className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded shadow-sm"
                                title="Editar"
                              >
                                <EditIcon sx={{ fontSize: 13 }} />
                              </button>
                              <button
                                onClick={() => {
                                  setPlatoAEliminar(plato);
                                  setOpenDialog(true);
                                }}
                                className="p-1 bg-red-500 hover:bg-red-600 text-white rounded shadow-sm"
                                title="Eliminar"
                              >
                                <DeleteIcon sx={{ fontSize: 13 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="h-48 bg-white rounded-xl flex flex-col items-center justify-center text-gray-400 border border-dashed border-gray-200 shadow-sm p-6 text-center">
              <BookIcon className="opacity-10 mb-3" sx={{ fontSize: 36 }} />
              <h3 className="text-sm font-bold text-gray-800">Selecciona una categoría</h3>
            </div>
          )}
        </div>
      </div>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} PaperProps={{ style: { borderRadius: '12px' } }}>
        <DialogTitle className="font-bold text-gray-800 text-base">¿Eliminar plato?</DialogTitle>
        <DialogContent>
          <p className="text-xs text-gray-600">Estás por eliminar <strong>{platoAEliminar?.nombre}</strong>.</p>
        </DialogContent>
        <DialogActions className="p-3 gap-2">
          <button onClick={() => setOpenDialog(false)} className="text-[10px] text-gray-500 px-3 py-1 font-bold uppercase">Cancelar</button>
          <button onClick={deletePlato} className="bg-red-600 text-white rounded px-4 py-1.5 text-[10px] font-bold">Eliminar</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AbmPlatos;
